import { useInitialLoading } from '../hooks/useInitialLoading';
import SalaryVersionsEditor from '../components/SalaryVersionsEditor';
import React, { useState, useEffect, useCallback } from 'react';
import { draftKey, readDraft, writeDraft, removeDraft } from '../utils/sessionDrafts';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' };
};

const STATUS_COLORS = {
  Active: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Draft: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
  'On Leave': { bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
  'On Probation': { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
  Suspended: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  Resigned: { bg: '#fce7f3', text: '#9d174d', border: '#f9a8d4' },
  Terminated: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  Archived: { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
};

const EMPLOYMENT_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Freelance', 'Intern'];
const COMPENSATION_TYPES = ['Monthly', 'Monthly + Commission', 'Hourly', 'Daily', 'Weekly', 'Per Project'];
const ATTENDANCE_MODES = ['Office', 'Remote', 'Hybrid', 'Field'];
const WORK_MODES = ['On-site', 'Remote', 'Hybrid'];

const toDateInputValue = (value) => {
  if (!value) return '';
  const match = String(value).match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : '';
};
const GENDERS = ['Male', 'Female'];
const NATIONALITIES = ['Lebanese', 'Afghan', 'Albanian', 'Algerian', 'American', 'Argentine', 'Armenian', 'Australian', 'Bangladeshi', 'Belgian', 'Brazilian', 'British', 'Canadian', 'Chinese', 'Cypriot', 'Egyptian', 'Emirati', 'Ethiopian', 'Filipino', 'French', 'German', 'Greek', 'Indian', 'Indonesian', 'Iranian', 'Iraqi', 'Irish', 'Italian', 'Japanese', 'Jordanian', 'Kenyan', 'Kuwaiti', 'Moroccan', 'Nepalese', 'Nigerian', 'Omani', 'Pakistani', 'Palestinian', 'Qatari', 'Romanian', 'Russian', 'Saudi', 'South African', 'Spanish', 'Sri Lankan', 'Sudanese', 'Syrian', 'Tunisian', 'Turkish', 'Ukrainian', 'Yemeni'];

export default function ProfileList() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [hrProfiles, setHrProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [deptFilter, setDeptFilter] = useState('');
  const [posFilter, setPosFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createTab, setCreateTab] = useState('personal');
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Identity files stay only in memory until the employee record has been saved.
  const [idUploadType, setIdUploadType] = useState('none');
  const [idFiles, setIdFiles] = useState({ front: null, back: null, passport: null });
  const isProjectBasedContract = form.employment_type === 'Contract' && form.compensation_type === 'Per Project';
  const isProjectBasedContractWithoutSalary = isProjectBasedContract && !(form.salary_versions?.length || form.new_salary_versions?.length);

  useEffect(() => {
    if (isProjectBasedContract && form.new_salary_versions?.length) {
      setForm(previous => ({ ...previous, new_salary_versions: [] }));
    }
  }, [isProjectBasedContract, form.new_salary_versions?.length]);

  // Edit modal
  const [editEmployee, setEditEmployee] = useState(null);

  const [draftNotice, setDraftNotice] = useState('');
  const currentDraftKey = draftKey(companyId, 'employee', editEmployee?.id || 'new');
  useEffect(() => {
    if (!showCreate) return;
    const saved = writeDraft(currentDraftKey, { form, tab: createTab });
    if (!saved) setDraftNotice('Automatic recovery is unavailable. Keep this form open until saved.');
  }, [showCreate, form, createTab, currentDraftKey]);
  const saveSessionDraft = async () => {
    const missingIdentityFile = identityUploadMissingMessage();
    if (missingIdentityFile) {
      setFieldErrors({ identity_document: [missingIdentityFile] });
      setCreateTab('personal');
      return;
    }
    setSaving(true);
    setFieldErrors({});
    try {
      const body = { ...prepareFormForSubmit(), save_as_draft: true };
      const url = editEmployee ? `${API}/companies/${companyId}/employees/${editEmployee.id}` : `${API}/companies/${companyId}/employees`;
      const res = await fetch(url, { method: editEmployee ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) {
        setFieldErrors(data.errors || { form: [data.message || 'Unable to save the draft.'] });
        return;
      }
      const uploadProblem = await uploadIdentityDocuments(data.data?.id || editEmployee?.id);
      removeDraft(currentDraftKey);
      setShowCreate(false);
      fetchAll();
      if (uploadProblem) alert(`The employee draft was saved, but ${uploadProblem}`);
    } catch {
      setFieldErrors({ form: ['Could not save the draft. Your form is still available in this browser session.'] });
    } finally { setSaving(false); }
  };

  // Bulk
  const [selected, setSelected] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkParams, setBulkParams] = useState({});

  // Archive/Delete confirm
  const [confirmAction, setConfirmAction] = useState(null);

  const handleAuthError = useCallback((status) => {
    if (status === 401 || status === 403) { navigate('/login'); return true; }
    return false;
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [empRes, deptRes, posRes, profileRes] = await Promise.all([
        fetch(`${API}/companies/${companyId}/employees`, { headers: getAuthHeaders() }),
        fetch(`${API}/companies/${companyId}/hr-departments`, { headers: getAuthHeaders() }),
        fetch(`${API}/companies/${companyId}/hr-positions`, { headers: getAuthHeaders() }),
        fetch(`${API}/companies/${companyId}/hr-profiles`, { headers: getAuthHeaders() }),
      ]);
      if (handleAuthError(empRes.status)) return;
      const [empData, deptData, posData, profileData] = await Promise.all([
        empRes.json(), deptRes.json(), posRes.json(), profileRes.json(),
      ]);
      setEmployees(empData.data || []);
      setDepartments((deptData.data || []).filter(d => d.status !== 'Archived'));
      setPositions((posData.data || []).filter(p => p.status !== 'Archived'));
      setHrProfiles(profileData.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, handleAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Filtering ──
  const filtered = employees.filter(e => {
    if (statusFilter !== 'All' && e.status !== statusFilter) return false;
    if (deptFilter && String(e.department_id) !== deptFilter) return false;
    if (posFilter && String(e.position_id) !== posFilter) return false;
    if (typeFilter && e.employment_type !== typeFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      const match = [e.first_name, e.middle_name, e.last_name, e.email, e.employee_code, e.phone]
        .filter(Boolean).some(v => v.toLowerCase().includes(s));
      if (!match) return false;
    }
    return true;
  });

  const statuses = ['All', ...new Set(employees.map(e => e.status).filter(Boolean))];
  const counts = { All: employees.length };
  employees.forEach(e => { counts[e.status] = (counts[e.status] || 0) + 1; });

  // ── Create / Edit ──
  const resetForm = () => {
    setForm({ status: 'Draft', employment_type: 'Full-Time', compensation_type: 'Monthly', attendance_mode: 'Office', work_mode: 'On-site' });
    setCreateTab('personal');
    setDuplicateWarning(null);
    setIdUploadType('none');
    setIdFiles({ front: null, back: null, passport: null });
  };

  const openCreate = () => {
    setFieldErrors({});
    const draft = readDraft(draftKey(companyId, 'employee'));
    resetForm();
    if (draft?.form) { setForm(draft.form); setCreateTab(draft.tab || 'personal'); }
    setDraftNotice(draft ? 'Recovered your unfinished employee draft.' : 'Changes are kept for this browser session when you close this form.');
    setShowCreate(true); setEditEmployee(null);
  };
  const openEdit = (emp) => {
    setFieldErrors({});
    setForm({
      ...emp,
      department_id: emp.department_id || '',
      position_id: emp.position_id || '',
      hr_profile_id: emp.hr_profile_id || '',
      hire_date: toDateInputValue(emp.hire_date),
      end_date: toDateInputValue(emp.end_date),
      date_of_birth: toDateInputValue(emp.date_of_birth),
    });
    const draft = readDraft(draftKey(companyId, 'employee', emp.id));
    if (draft?.form) setForm({
      ...draft.form,
      hire_date: toDateInputValue(draft.form.hire_date),
      end_date: toDateInputValue(draft.form.end_date),
      date_of_birth: toDateInputValue(draft.form.date_of_birth),
    });
    setDraftNotice(draft ? 'Recovered your unfinished changes.' : 'Changes are kept for this browser session.');
    setEditEmployee(emp);
    setShowCreate(true);
    setCreateTab('personal');
    setDuplicateWarning(null);
    setIdUploadType('none');
    setIdFiles({ front: null, back: null, passport: null });
  };

  const uploadIdentityDocuments = async (employeeId) => {
    if (!employeeId || idUploadType === 'none') return '';
    const documents = idUploadType === 'national_id'
      ? [
          { file: idFiles.front, category: 'National ID', title: 'National ID — Front' },
          { file: idFiles.back, category: 'National ID', title: 'National ID — Back' },
        ]
      : [{ file: idFiles.passport, category: 'Passport', title: 'Passport' }];

    try {
      const token = localStorage.getItem('token');
      const results = await Promise.all(documents.map(async ({ file, category, title }) => {
        const payload = new FormData();
        payload.append('file', file);
        payload.append('category', category);
        payload.append('title', title);
        payload.append('is_confidential', '1');
        const response = await fetch(`${API}/companies/${companyId}/employees/${employeeId}/documents`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          body: payload,
        });
        return response.ok;
      }));
      return results.every(Boolean) ? '' : 'one or more ID files could not be uploaded.';
    } catch {
      return 'the ID files could not be uploaded. You can add them from the employee profile.';
    }
  };

  const identityUploadMissingMessage = () => {
    if (idUploadType === 'national_id' && (!idFiles.front || !idFiles.back)) return 'Select both the front and back of the National ID.';
    if (idUploadType === 'passport' && !idFiles.passport) return 'Select the passport file.';
    return '';
  };

  const handleSave = async () => {
    const missingIdentityFile = identityUploadMissingMessage();
    if (missingIdentityFile) {
      setFieldErrors({ identity_document: [missingIdentityFile] });
      setCreateTab('personal');
      return;
    }
    setSaving(true);
    setDuplicateWarning(null);
    try {
      const isEdit = !!editEmployee;
      const url = isEdit
        ? `${API}/companies/${companyId}/employees/${editEmployee.id}`
        : `${API}/companies/${companyId}/employees`;
      const method = isEdit ? 'PUT' : 'POST';
      const body = prepareFormForSubmit();
      if (duplicateWarning) body.force_create = true;

      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (handleAuthError(res.status)) return;
      const data = await res.json();

      if (!res.ok) {
        if (data.requires_force) {
          setDuplicateWarning(data);
          setSaving(false);
          return;
        }
        if (data.requires_override) {
          const reason = window.prompt(`Headcount exceeded (${data.occupied_headcount}/${data.approved_headcount}). Enter override reason:`);
          if (reason) {
            body.override_headcount = true;
            body.override_reason = reason;
            const res2 = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
            const data2 = await res2.json();
            if (res2.ok) {
              const uploadProblem = await uploadIdentityDocuments(data2.data?.id || editEmployee?.id);
              removeDraft(currentDraftKey);
              setShowCreate(false);
              fetchAll();
              if (uploadProblem) alert(`The employee was saved, but ${uploadProblem}`);
            }
            else setFieldErrors(data2.errors || {form: [data2.message || 'Unable to save employee.']});
          }
          setSaving(false);
          return;
        }
        const errors = data.errors || {form: [data.message || 'Unable to save employee.']};
        setFieldErrors(errors);
        const keys = Object.keys(errors);
        const employment = ['department_id','position_id','hire_date','status','hr_profile_id','employment_type'];
        if (keys.some(k => k.startsWith('new_salary_versions'))) setCreateTab('salary');
        else if (keys.some(k => employment.includes(k))) setCreateTab('employment');
        else setCreateTab('personal');
        setSaving(false);
        return;
      }
      removeDraft(currentDraftKey);
      const uploadProblem = await uploadIdentityDocuments(data.data?.id || editEmployee?.id);
      setShowCreate(false);
      fetchAll();
      if (uploadProblem) alert(`The employee was saved, but ${uploadProblem}`);
    } catch (e) { setFieldErrors({form: ['Connection failed. Your form has been kept; please try again.']}); console.error(e); }
    setSaving(false);
  };

  // ── Actions ──
  const handleArchive = async (emp) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${emp.id}/archive`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({}),
      });
      if (handleAuthError(res.status)) return;
      if (res.ok) fetchAll();
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setConfirmAction(null);
  };

  const handleDelete = async (emp) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees/${emp.id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (handleAuthError(res.status)) return;
      if (res.ok) fetchAll();
      else { const d = await res.json(); alert(d.message); }
    } catch (e) { console.error(e); }
    setConfirmAction(null);
  };

  const handleBulk = async () => {
    if (!bulkAction || selected.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees-bulk`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ employee_ids: selected, action: bulkAction, ...bulkParams }),
      });
      if (handleAuthError(res.status)) return;
      const data = await res.json();
      if (res.ok) { alert(data.message); setSelected([]); setShowBulk(false); fetchAll(); }
      else alert(data.message || 'Error');
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map(e => e.id));
  };

  // ── Styles ──
  const s = {
    page: { padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1f2937', minHeight: '100vh', background: '#f9fafb' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    title: { fontSize: 22, fontWeight: 700, margin: 0 },
    headerRight: { display: 'flex', gap: 10, alignItems: 'center' },
    btn: { padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all 0.15s' },
    btnPrimary: { background: '#2563eb', color: '#fff' },
    btnDanger: { background: '#ef4444', color: '#fff' },
    btnOutline: { background: '#fff', color: '#374151', border: '1px solid #d1d5db' },
    btnSuccess: { background: '#16a34a', color: '#fff' },
    btnWarning: { background: '#f59e0b', color: '#fff' },
    cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 },
    card: (color) => ({ background: '#fff', borderRadius: 10, padding: '14px 18px', borderLeft: `4px solid ${color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }),
    cardLabel: { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
    cardVal: { fontSize: 24, fontWeight: 700, marginTop: 2 },
    filters: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
    pills: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    pill: (active) => ({ padding: '6px 14px', borderRadius: 20, border: active ? '2px solid #2563eb' : '1px solid #d1d5db', background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#374151', cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400, transition: 'all 0.15s' }),
    searchBox: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: 220, outline: 'none' },
    select: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', background: '#fff' },
    table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
    th: { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb', background: '#f9fafb' },
    td: { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
    badge: (colors) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: colors?.bg || '#f3f4f6', color: colors?.text || '#374151', border: `1px solid ${colors?.border || '#d1d5db'}` }),
    row: { cursor: 'pointer', transition: 'background 0.1s' },
    // Modal
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
    modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: 17, fontWeight: 700, margin: 0 },
    modalBody: { padding: '20px 24px' },
    modalFooter: { padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 },
    tabs: { display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
    tab: (active) => ({ padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#2563eb' : '#6b7280', borderBottom: active ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -2, transition: 'all 0.15s' }),
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 16 },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    formGroupFull: { display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1' },
    label: { fontSize: 12, fontWeight: 600, color: '#374151' },
    input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none' },
    fileLabel: { display: 'flex', flexDirection: 'column', gap: 6, padding: 12, border: '1px dashed #93c5fd', borderRadius: 8, color: '#1e3a5f', fontSize: 13, fontWeight: 600, background: '#f8fbff' },
    fileName: { color: '#64748b', fontWeight: 400 },
    helpText: { color: '#64748b', fontSize: 12, marginTop: 4 },
    textarea: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', minHeight: 60, resize: 'vertical', fontFamily: 'inherit' },
    dupWarning: { background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13 },
    bulkBar: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
    empty: { textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 },
    actionBtn: { padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12, marginRight: 4 },
  };

  // ── Summary Cards ──
  const summaryCards = [
    { label: 'Total Employees', value: employees.length, color: '#2563eb' },
    { label: 'Active', value: counts['Active'] || 0, color: '#16a34a' },
    { label: 'On Leave', value: counts['On Leave'] || 0, color: '#eab308' },
    { label: 'Draft', value: counts['Draft'] || 0, color: '#6b7280' },
    { label: 'Archived', value: counts['Archived'] || 0, color: '#9ca3af' },
  ];

  // ── Form Tabs Content ──
  const renderFormTab = () => {
    const f = (field, label, type = 'text', opts = {}) => (
      <div style={opts.full ? s.formGroupFull : s.formGroup} key={field}>
        <label style={{ ...s.label, color: fieldErrors[field] ? '#b91c1c' : s.label.color }}>{label}{opts.required && ' *'}</label>
        {type === 'select' ? (
          <select aria-invalid={!!fieldErrors[field]} style={{ ...s.input, borderColor: fieldErrors[field] ? '#dc2626' : '#d1d5db' }} value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}>
            <option value="">— Select —</option>
            {(opts.options || []).map(o => typeof o === 'string'
              ? <option key={o} value={o}>{o}</option>
              : <option key={o.value} value={o.value}>{o.label}</option>
            )}
          </select>
        ) : type === 'textarea' ? (
          <textarea aria-invalid={!!fieldErrors[field]} style={{ ...s.textarea, borderColor: fieldErrors[field] ? '#dc2626' : '#d1d5db' }} value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
        ) : (
          <input aria-invalid={!!fieldErrors[field]} style={{ ...s.input, borderColor: fieldErrors[field] ? '#dc2626' : '#d1d5db' }} type={type} value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
        )}
        {fieldErrors[field] && <small role="alert" style={{ color: '#b91c1c' }}>{fieldErrors[field].join(' ')}</small>}
      </div>
    );

    switch (createTab) {
      case 'personal':
        return (
          <div style={s.formGrid}>
            {f('first_name', 'First Name', 'text', { required: true })}
            {f('middle_name', 'Middle Name')}
            {f('last_name', 'Last Name', 'text', { required: true })}
            {f('email', 'Email (optional)', 'email')}
            {f('phone', 'Phone')}
            {f('date_of_birth', 'Date of Birth', 'date')}
            {f('gender', 'Gender', 'select', { options: GENDERS })}
            {f('nationality', 'Nationality', 'select', { options: [...new Set([...(form.nationality && !NATIONALITIES.includes(form.nationality) ? [form.nationality] : []), ...NATIONALITIES])] })}
            {f('national_id', 'National ID')}
            <div style={s.formGroupFull}>
              <label style={s.label}>Identity document upload</label>
              <select
                style={s.input}
                value={idUploadType}
                onChange={e => { setIdUploadType(e.target.value); setIdFiles({ front: null, back: null, passport: null }); }}
              >
                <option value="none">No document upload</option>
                <option value="national_id">National ID (front and back)</option>
                <option value="passport">Passport (one file)</option>
              </select>
              {idUploadType === 'national_id' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 8 }}>
                  <label style={s.fileLabel}>National ID — front
                    <input type="file" accept="image/*,.pdf" onChange={e => setIdFiles(p => ({ ...p, front: e.target.files?.[0] || null }))} />
                    <small style={s.fileName}>{idFiles.front?.name || 'Choose image or PDF'}</small>
                  </label>
                  <label style={s.fileLabel}>National ID — back
                    <input type="file" accept="image/*,.pdf" onChange={e => setIdFiles(p => ({ ...p, back: e.target.files?.[0] || null }))} />
                    <small style={s.fileName}>{idFiles.back?.name || 'Choose image or PDF'}</small>
                  </label>
                </div>
              )}
              {idUploadType === 'passport' && (
                <label style={{ ...s.fileLabel, marginTop: 8 }}>Passport
                  <input type="file" accept="image/*,.pdf" onChange={e => setIdFiles(p => ({ ...p, passport: e.target.files?.[0] || null }))} />
                  <small style={s.fileName}>{idFiles.passport?.name || 'Choose image or PDF'}</small>
                </label>
              )}
              {idUploadType !== 'none' && <small style={s.helpText}>The selected document is saved privately with this employee when you save the form.</small>}
              {fieldErrors.identity_document && <small role="alert" style={{ color: '#b91c1c' }}>{fieldErrors.identity_document.join(' ')}</small>}
            </div>
            {f('address', 'Address', 'textarea', { full: true })}
            {f('emergency_contact_name', 'Emergency Contact Name')}
            {f('emergency_contact_phone', 'Emergency Contact Phone')}
            {f('notes', 'Notes', 'textarea', { full: true })}
          </div>
        );
      case 'employment':
        return (
          <div style={s.formGrid}>
            {f('department_id', 'Department *', 'select', { required: true, options: departments.map(d => ({ value: d.id, label: d.name })) })}
            {f('position_id', 'Position *', 'select', { required: true, options: positions.map(p => ({ value: p.id, label: p.title })) })}
            {f('hr_profile_id', 'HR Profile', 'select', { options: hrProfiles.map(p => ({ value: p.id, label: p.name })) })}
            {f('status', 'Status', 'select', { options: ['Draft', 'Active', 'On Leave', 'On Probation', 'Suspended', 'Resigned', 'Terminated'] })}
            {f('hire_date', 'Hire Date *', 'date', { required: true })}
            {f('end_date', 'End Date', 'date')}
            {f('employment_type', 'Employment Type', 'select', { options: EMPLOYMENT_TYPES })}
            {f('compensation_type', 'Compensation Arrangement', 'select', { options: COMPENSATION_TYPES })}
            {f('attendance_mode', 'Attendance Mode', 'select', { options: ATTENDANCE_MODES })}
            {f('work_mode', 'Work Mode', 'select', { options: WORK_MODES })}
            {isProjectBasedContract && <div style={{ ...s.formGroupFull, padding: 14, borderRadius: 8, background: '#eff6ff', color: '#1e3a8a', fontSize: 13 }}>
              <strong>Project-based contract</strong>
              <span>Salary and fixed compensation are managed directly inside each project or academy cohort. No salary version is needed for this employee.</span>
            </div>}
            {f('probation_period', 'Probation Period (days)', 'number')}
            {f('notice_period', 'Notice Period (days)', 'number')}
          </div>
        );
      case 'salary':
        return isProjectBasedContractWithoutSalary ? <div style={{ padding: 16, borderRadius: 8, background: '#eff6ff', color: '#1e3a8a', lineHeight: 1.5 }}>
          This employee is a project-based contractor. Enter their compensation directly on each project or academy cohort.
        </div> : <div style={s.formGrid}>
          <SalaryVersionsEditor form={form} setForm={setForm} errors={fieldErrors} />
          {f('transportation_rate', 'Transportation Rate', 'number')}
        </div>;
      default: return null;
    }
  };

  const prepareFormForSubmit = () => {
    const body = { ...form };
    const editedVersions = (form.salary_versions || []).filter(version => version._edited).map(({ id, amount, effective_date, reason, employment_type, compensation_type, attendance_mode, work_mode }) => ({ id, amount, effective_date: toDateInputValue(effective_date), reason: reason || 'Initial salary', employment_type, compensation_type, attendance_mode, work_mode }));
    ['allowances', 'commissions', 'fixed_bonuses', 'recurring_deductions', 'salary_versions'].forEach(field => delete body[field]);
    if (editedVersions.length) body.edited_salary_versions = editedVersions;
    if (isProjectBasedContract) {
      delete body.new_salary_versions;
      delete body.salary;
      delete body.transportation_rate;
    }
    if (!isProjectBasedContract && (body.new_salary_versions?.length || form.salary_versions?.length)) ['salary', 'employment_type', 'compensation_type', 'attendance_mode', 'work_mode'].forEach(field => delete body[field]);
    return body;
  };

  if (initialLoading) return <div style={s.page}><p>Loading employees…</p></div>;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>👥 Employee Directory</h1>
        <div style={s.headerRight}>
          {selected.length > 0 && (
            <button style={{ ...s.btn, ...s.btnWarning }} onClick={() => setShowBulk(true)}>
              ⚡ Bulk Actions ({selected.length})
            </button>
          )}
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={openCreate}>+ Add Employee</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={s.cards}>
        {summaryCards.map(c => (
          <div key={c.label} style={s.card(c.color)}>
            <div style={s.cardLabel}>{c.label}</div>
            <div style={{ ...s.cardVal, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <div style={s.pills}>
          {statuses.map(st => (
            <span key={st} style={s.pill(statusFilter === st)} onClick={() => setStatusFilter(st)}>
              {st} ({counts[st] || 0})
            </span>
          ))}
        </div>
      </div>
      <div style={s.filters}>
        <input style={s.searchBox} placeholder="🔍 Search name, code, email, phone…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select style={s.select} value={posFilter} onChange={e => setPosFilter(e.target.value)}>
          <option value="">All Positions</option>
          {positions.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <select style={s.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Bulk Selection Bar */}
      {selected.length > 0 && (
        <div style={s.bulkBar}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>✅ {selected.length} employee(s) selected</span>
          <button style={{ ...s.btn, ...s.btnOutline, fontSize: 12 }} onClick={() => setSelected([])}>Clear Selection</button>
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleAll} /></th>
              <th style={s.th}>Code</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Department</th>
              <th style={s.th}>Position</th>
              <th style={s.th}>Type</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Hire Date</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={s.empty}>No employees found.</td></tr>
            ) : filtered.map(emp => {
              const sc = STATUS_COLORS[emp.status] || STATUS_COLORS.Draft;
              return (
                <tr key={emp.id} style={s.row}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={s.td} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(emp.id)} onChange={() => toggleSelect(emp.id)} />
                  </td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}
                    onClick={() => navigate(`/employees/${emp.id}`)}>
                    {emp.employee_code || '—'}
                  </td>
                  <td style={{ ...s.td, fontWeight: 600 }} onClick={() => navigate(`/employees/${emp.id}`)}>
                    {emp.first_name} {emp.middle_name} {emp.last_name}
                  </td>
                  <td style={s.td} onClick={() => navigate(`/employees/${emp.id}`)}>
                    {emp.department?.name || '—'}
                  </td>
                  <td style={s.td} onClick={() => navigate(`/employees/${emp.id}`)}>
                    {emp.position?.title || '—'}
                  </td>
                  <td style={s.td} onClick={() => navigate(`/employees/${emp.id}`)}>
                    {emp.employment_type || '—'}
                  </td>
                  <td style={s.td} onClick={() => navigate(`/employees/${emp.id}`)}>
                    <span style={s.badge(sc)}>{emp.status}</span>
                  </td>
                  <td style={s.td} onClick={() => navigate(`/employees/${emp.id}`)}>
                    {emp.hire_date ? new Date(emp.hire_date).toLocaleDateString() : '—'}
                  </td>
                  <td style={s.td} onClick={e => e.stopPropagation()}>
                    <button style={s.actionBtn} title="Edit" onClick={() => openEdit(emp)}>✏️</button>
                    {emp.status !== 'Archived' && (
                      <button style={s.actionBtn} title="Archive" onClick={() => setConfirmAction({ type: 'archive', employee: emp })}>📦</button>
                    )}
                    {emp.status === 'Draft' && (
                      <button style={{ ...s.actionBtn, color: '#ef4444' }} title="Delete" onClick={() => setConfirmAction({ type: 'delete', employee: emp })}>🗑️</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={{ ...s.modal, maxWidth: 1080, width: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...s.modalHeader, flexShrink: 0 }}>
              <h2 style={s.modalTitle}>{editEmployee ? `Edit: ${[editEmployee.first_name, editEmployee.middle_name, editEmployee.last_name].filter(Boolean).join(' ')}` : 'Add New Employee'}</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <div style={{ ...s.tabs, flexShrink: 0, flexWrap: 'wrap', marginBottom: 0 }}>
              {[
                { key: 'personal', label: '👤 Personal Info' },
                { key: 'employment', label: '💼 Employment' },
                ...(!isProjectBasedContractWithoutSalary ? [{ key: 'salary', label: '💰 Salary' }] : []),
              ].map(t => (
                <div key={t.key} style={s.tab(createTab === t.key)} onClick={() => setCreateTab(t.key)}>{t.label}</div>
              ))}
            </div>
            <div style={{ ...s.modalBody, minHeight: 0, overflowY: 'auto' }}>
              {!!Object.keys(fieldErrors).length && <div role="alert" style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:14, color:'#b91c1c', marginBottom:16 }}>
                <strong>Please correct these fields:</strong><ul style={{ margin:'8px 0 0', paddingLeft:20 }}>{Object.entries(fieldErrors).flatMap(([field,messages]) => messages.map((message,i)=><li key={`${field}-${i}`}>{message}</li>))}</ul>
              </div>}
              {duplicateWarning && (
                <div style={s.dupWarning}>
                  <strong>⚠️ Duplicate Found:</strong> {duplicateWarning.message}<br />
                  <span style={{ fontSize: 12 }}>Employee: {duplicateWarning.duplicate_employee?.first_name} {duplicateWarning.duplicate_employee?.last_name}</span><br />
                  <button style={{ ...s.btn, ...s.btnWarning, marginTop: 8, fontSize: 12 }} onClick={handleSave}>
                    Create Anyway (Force)
                  </button>
                </div>
              )}
              <p role="status" style={{ padding: '8px 0', color: '#475569' }}>{draftNotice}</p>
              {renderFormTab()}
            </div>
            <div style={{ ...s.modalFooter, flexShrink: 0, flexWrap: 'wrap' }}>
              <button style={{ ...s.btn, ...s.btnOutline }} disabled={saving} onClick={() => setShowCreate(false)}>Close</button>
              <button style={{ ...s.btn, ...s.btnOutline }} disabled={saving} onClick={saveSessionDraft}>{saving ? 'Saving…' : 'Save as Draft'}</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving}
                onClick={handleSave}>
                {saving ? 'Saving…' : editEmployee ? 'Update Employee' : 'Create Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Archive/Delete Modal */}
      {confirmAction && (
        <div style={s.overlay} onClick={() => setConfirmAction(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {confirmAction.type === 'archive' ? '📦 Archive Employee' : '🗑️ Delete Employee'}
              </h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setConfirmAction(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, fontSize: 14 }}>
                {confirmAction.type === 'archive'
                  ? `Are you sure you want to archive "${confirmAction.employee.first_name} ${confirmAction.employee.last_name}"? They will be removed from active lists but data is preserved.`
                  : `Permanently delete "${confirmAction.employee.first_name} ${confirmAction.employee.last_name}"? This cannot be undone. Only Draft employees can be deleted.`
                }
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setConfirmAction(null)}>Cancel</button>
              <button style={{ ...s.btn, ...(confirmAction.type === 'archive' ? s.btnWarning : s.btnDanger) }}
                onClick={() => confirmAction.type === 'archive' ? handleArchive(confirmAction.employee) : handleDelete(confirmAction.employee)}>
                {confirmAction.type === 'archive' ? 'Archive' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Modal */}
      {showBulk && (
        <div style={s.overlay} onClick={() => setShowBulk(false)}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>⚡ Bulk Actions ({selected.length} employees)</h2>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowBulk(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Action</label>
                <select style={s.input} value={bulkAction} onChange={e => { setBulkAction(e.target.value); setBulkParams({}); }}>
                  <option value="">— Select Action —</option>
                  <option value="change_status">Change Status</option>
                  <option value="assign_department">Assign Department</option>
                  <option value="assign_hr_profile">Assign HR Profile</option>
                  <option value="archive">Archive All</option>
                </select>
              </div>
              {bulkAction === 'change_status' && (
                <div style={{ ...s.formGroup, marginTop: 12 }}>
                  <label style={s.label}>New Status</label>
                  <select style={s.input} value={bulkParams.status || ''} onChange={e => setBulkParams(p => ({ ...p, status: e.target.value }))}>
                    <option value="">— Select —</option>
                    {['Active', 'On Leave', 'On Probation', 'Suspended', 'Resigned', 'Terminated'].map(st =>
                      <option key={st} value={st}>{st}</option>
                    )}
                  </select>
                </div>
              )}
              {bulkAction === 'assign_department' && (
                <div style={{ ...s.formGroup, marginTop: 12 }}>
                  <label style={s.label}>Department</label>
                  <select style={s.input} value={bulkParams.department_id || ''} onChange={e => setBulkParams(p => ({ ...p, department_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}
              {bulkAction === 'assign_hr_profile' && (
                <div style={{ ...s.formGroup, marginTop: 12 }}>
                  <label style={s.label}>HR Profile</label>
                  <select style={s.input} value={bulkParams.hr_profile_id || ''} onChange={e => setBulkParams(p => ({ ...p, hr_profile_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {hrProfiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => setShowBulk(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving || !bulkAction ? 0.6 : 1 }}
                disabled={saving || !bulkAction} onClick={handleBulk}>
                {saving ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
